<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { useConfigStore } from "@/stores/store.config";
import BaseModal from "@/components/BaseModal.vue";
import VIcon from "@/components/VIcon.vue";

const props = withDefaults(
  defineProps<{
    open?: boolean;
    name?: string;
    email?: string;
    phoneNumber?: string;
    roomNumber?: string | number;
    title?: string;
    description?: string;
    icon?: string;
    showStar?: boolean;
    genericQrCode?: boolean;
    error?: string;
    submitting?: boolean;
  }>(),
  { open: true },
);
const emit = defineEmits<{
  close: [];
  submit: [
    guest: {
      name: string;
      email: string;
      phoneNumber: string;
      roomNumber: string;
      enableNotification: boolean;
    },
  ];
}>();

const user = useConfigStore().useUserStore();
const labels = computed(() => user.currentLang.labels || {});
const config = computed(() => user.homepageConfig?.guestForm || {});
const guest = reactive({
  name: props.name || "",
  email: props.email || "",
  phoneNumber: props.phoneNumber || "",
  roomNumber: String(props.roomNumber ?? ""),
  enableNotification: true,
});

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    Object.assign(guest, {
      name: props.name || "",
      email: props.email || "",
      phoneNumber: props.phoneNumber || "",
      roomNumber: String(props.roomNumber ?? ""),
      enableNotification: true,
    });
  },
);

const isEnabled = (field: "name" | "email" | "phone") =>
  config.value[`${field}IsEnabled`] !== false &&
  config.value[`${field}IsEnabled`] !== "false";
const isRequired = (field: "name" | "email" | "phone") =>
  isEnabled(field) &&
  (config.value[`${field}IsRequired`] === true ||
    config.value[`${field}IsRequired`] === "true");
const canEditRoomNumber = computed(
  () =>
    props.genericQrCode === true ||
    user.homepageConfig?.secureRoomVerificationEnabled === false,
);
const requiresRoomNumber = computed(() => props.genericQrCode === true);
const submit = () =>
  emit("submit", {
    ...guest,
    email: isEnabled("email") ? guest.email : "",
    phoneNumber: isEnabled("phone") ? guest.phoneNumber : "",
  });
</script>

<template>
  <BaseModal
    :open="open"
    size="medium"
    :aria-label="title || labels.Rateus_label || 'Rate us'"
    @close="emit('close')"
  >
    <form class="guest-card" @submit.prevent="submit">
      <button
        type="button"
        class="close"
        :aria-label="labels.Close || 'Close'"
        @click="emit('close')"
      >
        <VIcon icon="cross" />
      </button>
      <div v-if="showStar !== false || icon" class="guest-star">
        <VIcon :icon="icon || 'star'" />
      </div>
      <div class="guest-heading">
        <h2>{{ title || labels.Rateus_label || "Rate us" }}</h2>
        <p>
          {{ description || labels.Form_desc || "Please provide your details" }}
        </p>
      </div>
      <label
        >{{ labels.Formname_label || "Name"
        }}<span v-if="isRequired('name')">*</span
        ><input
          v-model="guest.name"
          :placeholder="labels.Formname_textplaceholder || 'Name'"
          :required="isRequired('name')"
      /></label>
      <label
        >{{ labels.Formroom_label || "Room number"
        }}<span v-if="requiresRoomNumber">*</span
        ><input
          v-model="guest.roomNumber"
          :placeholder="labels.Formroom_textplaceholder || 'Room number'"
          :readonly="!canEditRoomNumber"
          :class="{ readonly: !canEditRoomNumber }"
          :required="requiresRoomNumber"
      /></label>
      <label v-if="isEnabled('email')"
        >{{ labels.Formemail_label || "Email"
        }}<span v-if="isRequired('email')">*</span
        ><input
          v-model="guest.email"
          type="email"
          :placeholder="labels.Formemail_textplaceholder || 'Email'"
          :required="isRequired('email')"
      /></label>
      <label v-if="isEnabled('phone')"
        >{{ labels.Formphone_label || "Phone number"
        }}<span v-if="isRequired('phone')">*</span
        ><input
          v-model="guest.phoneNumber"
          type="tel"
          :placeholder="labels.Formphone_textplaceholder || 'Phone number'"
          :required="isRequired('phone')"
      /></label>
      <label class="notification"
        ><input v-model="guest.enableNotification" type="checkbox" />{{
          labels.Formnotif_dec || "Enable notifications"
        }}</label
      >
      <p v-if="error" role="alert">{{ error }}</p>
      <button class="submit" type="submit" :disabled="submitting">
        {{ labels.Proceed_button || "Proceed" }}
      </button>
    </form>
  </BaseModal>
</template>

<style scoped>
.guest-card {
  position: relative;
}
.guest-star {
  display: flex;
  justify-content: center;
}
.guest-heading {
  text-align: center;
}
.guest-heading h2 {
  margin: 10px 0;
  color: #333;
  font-size: var(--font-size-subheading);
  font-weight: var(--font-weight-medium);
}
.guest-heading p {
  margin: 0 0 20px;
  font-size: var(--font-size-caption);
}
label {
  display: block;
  margin: 12px 0 0;
  color: #333;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}
label span {
  color: #e5484d;
}
input {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  margin-top: 6px;
  padding: 0 10px;
  border: 1px solid #cecece;
  border-radius: 0;
  font: inherit;
}
.readonly {
  background: #eaeaea;
}
.close {
  position: absolute;
  top: 8px;
  right: 10px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.notification {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 22px;
  font-weight: var(--font-weight-regular);
}
.notification input {
  width: auto;
  height: auto;
  margin: 0;
}
.submit {
  width: 100%;
  min-height: 40px;
  margin: 8px 0 0;
  border: 0;
  border-radius: 8px;
  background: #3377ff;
  color: #fff;
  cursor: pointer;
  font-family: var(--font-sans);
  font-weight: var(--font-weight-bold);
}
</style>
